/**
 * ResetPasswordForm Component
 * 
 * Password reset form with strength validation.
 * Used after user clicks reset link from email.
 * 
 * Requirements: 3.2, 2.5
 */

import React, { useState } from 'react';
import { validatePasswordStrength } from '../utils/validation.utils';
import { ErrorAlert } from './ErrorAlert';
import { LoadingSpinner } from './LoadingSpinner';
import { PasswordStrength } from './PasswordStrength';

export interface ResetPasswordFormProps {
  email: string;
  code: string;
  onSubmit: (email: string, code: string, newPassword: string) => Promise<void>;
  onBackToLogin?: () => void;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ 
  email,
  code,
  onSubmit, 
  onBackToLogin 
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    const passwordValidation = validatePasswordStrength(newPassword);
    if (!passwordValidation.isValid) {
      newErrors.push(...passwordValidation.feedback);
    }

    if (newPassword !== confirmPassword) {
      newErrors.push('Passwords do not match');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors([]);

    try {
      await onSubmit(email, code, newPassword);
      setIsSuccess(true);
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Password reset failed']);
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #3498db',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    color: '#333333',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: isLoading ? 'not-allowed' : 'pointer',
    opacity: isLoading ? 0.7 : 1,
  };

  if (isSuccess) {
    return (
      <div className="reset-password-success">
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          border: '1px solid #c8e6c9',
          borderRadius: '4px',
          padding: '16px',
          marginBottom: '24px',
          color: '#2e7d32',
        }}>
          <strong>Password reset successful!</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Your password has been updated. You can now sign in with your new password.
          </p>
        </div>
        
        {onBackToLogin && (
          <button
            type="button"
            onClick={onBackToLogin}
            style={buttonStyle}
          >
            Go to Login
          </button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="reset-password-form">
      {errors.length > 0 && <ErrorAlert message={errors} onDismiss={() => setErrors([])} />}

      <p style={{ marginBottom: '24px', fontSize: '14px', color: '#666' }}>
        Enter your new password below.
      </p>

      <div style={{ marginBottom: '16px' }}>
        <label htmlFor="newPassword" style={labelStyle}>
          New Password
        </label>
        <input
          id="newPassword"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="new-password"
          style={inputStyle}
          aria-required="true"
        />
        <PasswordStrength password={newPassword} />
      </div>

      <div style={{ marginBottom: '24px' }}>
        <label htmlFor="confirmPassword" style={labelStyle}>
          Confirm New Password
        </label>
        <input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="new-password"
          style={inputStyle}
          aria-required="true"
        />
      </div>

      <button type="submit" disabled={isLoading} style={buttonStyle}>
        {isLoading ? <LoadingSpinner size="small" /> : 'Reset Password'}
      </button>
    </form>
  );
};
